import {
  DEFAULT_INVOICE_TEMPLATE,
  DEFAULT_RECEIPT_TEMPLATE,
  DEFAULT_INVOICE_LABELS,
  DEFAULT_RECEIPT_LABELS,
} from './receiptTemplate';
import i18n from '../i18n';
import { paymentMethodLabel, paymentStatusLabel } from './statusLabel';
import { isOwnFrameName } from './itemDisplayName';

// Printed documents open in a new window outside React, so we read the active
// language straight from the i18n singleton rather than the hook.
const tr = (key, opts) => i18n.t(key, opts);

export function escapeHtml(str) {
  if (str == null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatMoneyDzd(n) {
  if (n == null || n === '' || Number.isNaN(Number(n))) return '—';
  return `${Number(n).toLocaleString()} DZD`;
}

function fullName(person) {
  if (!person || typeof person !== 'object') return '';
  return [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
}

function formatDate(date) {
  if (!date) return '—';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function collectItems(orderData) {
  for (const key of ['items', 'order_items', 'orderItems']) {
    if (Array.isArray(orderData?.[key]) && orderData[key].length) return orderData[key];
  }
  // The order_items table insert can lag/fail, but the line items are always
  // mirrored into the order's raw_data — fall back to that.
  if (Array.isArray(orderData?.raw_data?.items) && orderData.raw_data.items.length) return orderData.raw_data.items;
  try {
    const summary = JSON.parse(sessionStorage.getItem('orderSummary') || '{}');
    if (Array.isArray(summary.items) && summary.items.length) return summary.items;
  } catch (_) {}
  return [];
}

// A two-pair sale ("Both": far + close up) is one order whose lines name their
// pair (specifications.visionPair). Pair 2's measures sit under
// measurements.near. Returns null for an ordinary one-pair order.
function pairsOf(orderData) {
  const items = collectItems(orderData);
  if (!items.some((item) => item.specifications?.visionPair === 'near')) return null;
  const m = orderData?.measurements || {};
  const { near, ...far } = m;
  return [
    { visionPair: 'far', orderNumber: orderData.orderNumber, measurements: far },
    { visionPair: 'near', orderNumber: orderData.orderNumber, measurements: near || {} },
  ];
}

const pairLabelOf = (item) => {
  const pair = item?.specifications?.visionPair;
  return pair ? tr(pair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar') : '';
};

function findItem(orderData, type) {
  return collectItems(orderData).find((item) => (item.type || item.item_type) === type) || null;
}

function formatEyeLineReceipt(eye) {
  if (!eye) return null;
  const { sphere, cylinder, axis } = eye;
  if (sphere === '' || sphere === null || sphere === undefined) return null;
  const fmt = (v) => { const n = Number(v); return Number.isFinite(n) ? (n >= 0 ? `+${n.toFixed(2)}` : n.toFixed(2)) : null; };
  const parts = [
    fmt(sphere) ? `Sph ${fmt(sphere)}` : null,
    cylinder !== '' && cylinder !== null && cylinder !== undefined ? `Cyl ${fmt(cylinder) || cylinder}` : null,
    axis !== '' && axis !== null && axis !== undefined ? `Ax ${axis}°` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' / ') : null;
}

function getCorrectionLines(orderData) {
  const p = orderData?.prescription;
  if (p) {
    // The API spreads correction fields onto the prescription itself
    // (rightEyeFar, leftEyeFar, …); older shapes nest them under `corrections`.
    const c = p.corrections || p;
    const isProgressive = (p.prescriptionType || p.prescription_type) === 'progressive';
    const od = isProgressive
      ? (c.progressiveRightEyeFar || c.rightEyeFar || c.rightEye || c.rightEyeCloseUp)
      : (c.rightEyeFar || c.rightEye || c.rightEyeCloseUp);
    const os = isProgressive
      ? (c.progressiveLeftEyeFar || c.leftEyeFar || c.leftEye || c.leftEyeCloseUp)
      : (c.leftEyeFar || c.leftEye || c.leftEyeCloseUp);
    const odLine = formatEyeLineReceipt(od);
    const osLine = formatEyeLineReceipt(os);
    if (odLine || osLine) return { od: odLine, os: osLine };
  }
  try {
    const pfd = JSON.parse(sessionStorage.getItem('prescriptionFormData') || '{}');
    return {
      od: formatEyeLineReceipt(pfd.rightEyeFar || pfd.rightEye || pfd.rightEyeCloseUp),
      os: formatEyeLineReceipt(pfd.leftEyeFar  || pfd.leftEye  || pfd.leftEyeCloseUp),
    };
  } catch (_) { return { od: null, os: null }; }
}

// One pair's correction in a two-pair sale: the far pair reads the far rows,
// the close up pair the close up rows.
function getPairCorrectionLines(orderData, visionPair) {
  let c = orderData?.prescription?.corrections || orderData?.prescription || null;
  if (!c || !(c.rightEyeFar || c.rightEyeCloseUp)) {
    try { c = JSON.parse(sessionStorage.getItem('prescriptionFormData') || '{}'); } catch (_) { c = {}; }
  }
  const near = visionPair === 'near';
  return {
    od: formatEyeLineReceipt(near ? c.rightEyeCloseUp : c.rightEyeFar),
    os: formatEyeLineReceipt(near ? c.leftEyeCloseUp : c.leftEyeFar),
  };
}

function itemDisplayName(item) {
  if (!item) return '—';
  // Lenses print under the store's commercial name, never the supplier's real
  // name (item.name stays the real one because stock matching depends on it).
  const commercial = item.specifications?.commercialName || item.commercialName;
  // A two-pair sale's lines say which pair they belong to.
  const pair = pairLabelOf(item);
  const withPair = (text) => (pair ? `${text} (${pair})` : text);
  if (commercial && (item.type === 'lens' || item.item_type === 'lens')) return withPair(commercial);
  return withPair([item.name, item.brand, item.model].filter(Boolean).join(' · '));
}

// A customer can leave with more than one frame (spares/backups bought
// alongside the prescription pair) — every frame-typed line still gets its
// own stock deduction, but the receipt/passport only had room for exactly
// one frame name. Show the first and note how many more rather than
// silently dropping the rest of what the customer paid for.
function frameSummaryDisplayName(orderData) {
  const frames = collectItems(orderData).filter((item) => (item.type || item.item_type) === 'frame');
  if (!frames.length) return itemDisplayName(null);
  // A two-pair sale lists every frame: each one is a pair, not a spare.
  if (pairsOf(orderData)) return frames.map(itemDisplayName).join(' + ');
  const [first, ...rest] = frames;
  const firstName = itemDisplayName(first);
  return rest.length ? `${firstName} (+${rest.length})` : firstName;
}

// Longest lead time across the order's lines — in practice the lens, since a
// frame in stock is same-day. Returns null when nothing declares a lead time,
// so callers can print a blank rather than invent a date.
function orderDeliveryDays(orderData) {
  const days = (Array.isArray(orderData?.items) ? orderData.items : []).reduce((max, item) => {
    const value = Number(item?.specifications?.estimatedDays || item?.estimatedDays || 0);
    return Number.isFinite(value) ? Math.max(max, value) : max;
  }, 0);
  return days > 0 ? days : null;
}

// The shop works six days a week — Friday is the only day off — so a lead time
// quoted in days has to step over Fridays to land on a real date.
const NON_WORKING_DAY = 5; // Date#getDay(): 0 Sun … 5 Fri, 6 Sat

function addWorkingDays(startDate, days) {
  const date = new Date(startDate);
  if (Number.isNaN(date.getTime())) return null;
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    if (date.getDay() !== NON_WORKING_DAY) added += 1;
  }
  return date;
}

// Progressive with trial lenses ("verre d'essai", ticked on the prescription):
// the passport shows it to MG Central and delivery takes 10 more CALENDAR
// days (every day counts, unlike the lens lead time).
export const TRIAL_LENS_EXTRA_DAYS = 10;

export function hasTrialLens(orderData) {
  const p = orderData?.prescription;
  if (p && typeof p === 'object') {
    return (p.prescriptionType || p.prescription_type) === 'progressive'
      && (p.trialLens === true || p.trialLens === 'true' || p.raw_data?.trialLens === true);
  }
  // Right after a sale the order may only carry the prescription id; the
  // form is still in the session then — but only trust it when it is THIS
  // order's prescription (a dashboard on the same device must never pick up
  // another client's sale).
  try {
    const orderRxId = typeof p === 'string' ? p : (orderData?.raw_data?.prescription || orderData?.prescriptionId || '');
    const sessionRxId = sessionStorage.getItem('prescriptionId') || '';
    if (!orderRxId || String(orderRxId) !== String(sessionRxId)) return false;
    const pfd = JSON.parse(sessionStorage.getItem('prescriptionFormData') || '{}');
    return pfd.prescriptionType === 'progressive' && pfd.trialLens === true;
  } catch (_) {
    return false;
  }
}

// Lead time to quote, in days: the lens lead time plus the trial-lens days.
function totalDeliveryDays(orderData) {
  const total = (orderDeliveryDays(orderData) || 0) + (hasTrialLens(orderData) ? TRIAL_LENS_EXTRA_DAYS : 0);
  return total > 0 ? total : null;
}

// Date the equipment is due back: the lens lead time in working days from the
// sale, then the trial-lens days as calendar days.
function expectedDeliveryDate(orderData) {
  if (orderData?.estimatedCompletion) return new Date(orderData.estimatedCompletion);
  const start = orderData?.orderDate || orderData?.createdAt || Date.now();
  const days = orderDeliveryDays(orderData);
  const trial = hasTrialLens(orderData);
  if (!days && !trial) return null;
  const date = days ? addWorkingDays(start, days) : new Date(start);
  if (!date || Number.isNaN(date.getTime())) return null;
  if (trial) date.setDate(date.getDate() + TRIAL_LENS_EXTRA_DAYS);
  return date;
}

function estimatedDelivery(orderData) {
  if (orderData.estimatedCompletion) return formatDate(orderData.estimatedCompletion);
  const days = totalDeliveryDays(orderData);
  return days ? i18n.t('common.dayCount', { count: days }) : i18n.t('common.defaultDeliveryWindow');
}

function buildQrImage(value, caption) {
  const text = escapeHtml(caption ?? value ?? '');
  const src  = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=2&data=${encodeURIComponent(value || '')}`;
  return `<div class="qr-box">
    <img class="qr-img" src="${src}" alt="QR code for ${text}" />
    <div class="qr-caption">${text}</div>
  </div>`;
}

// ─── Default / Optiva branding ─────────────────────────────────────────────────
const RECEIPT_DEFAULTS = {
  storeName:          'Optiva',
  tagline:            'Optical Excellence',
  primaryColor:       '#10b981',
  accentColor:        '#6366f1',
  logo:               null,
  phone:              '',
  email:              '',
  city:               '',
  invoiceTemplate:    {},
  receiptTemplate:    {},
};

function getStoreIdFrom(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value._id || value.id || '';
  return '';
}

function getOrderStoreId(orderData) {
  const fromOrder = getStoreIdFrom(orderData?.store);
  if (fromOrder) return fromOrder;
  try {
    const stored = JSON.parse(localStorage.getItem('user') || 'null');
    return getStoreIdFrom(stored?.store || stored?.storeId);
  } catch (_) {
    return '';
  }
}

async function fetchStoreBrandingForOrder(orderData) {
  const storeId = getOrderStoreId(orderData);
  if (!storeId || typeof fetch !== 'function') return null;
  try {
    const res = await fetch(`/api/stores/${storeId}/branding`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.branding || null;
  } catch (_) {
    return null;
  }
}

// An invoice carries its own number (server-assigned once, per store and
// year). Ask for it before building; if that fails the invoice still prints,
// with "N° —".
async function ensureInvoiceNumber(orderData, reimbursementRefs = null) {
  const id = orderData?._id || orderData?.id;
  // od / og: the pair's eyes (pair 1 of a two-pair sale); nearOd / nearOg:
  // pair 2 (close up), only on a two-pair sale.
  const refsBody = reimbursementRefs
    ? Object.fromEntries(['od', 'og', 'nearOd', 'nearOg'].filter((k) => reimbursementRefs[k] != null).map((k) => [k, reimbursementRefs[k]]))
    : null;
  const withRefs = refsBody ? { ...orderData, reimbursementRefs: refsBody } : orderData;
  if (!id || typeof fetch !== 'function') return withRefs;
  if (!reimbursementRefs && (orderData.invoiceNumber || orderData.raw_data?.invoiceNumber)) return withRefs;
  try {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/orders/${id}/invoice-number`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(refsBody ? { reimbursementRefs: refsBody } : {}),
    });
    if (!res.ok) return withRefs;
    const data = await res.json();
    return {
      ...withRefs,
      ...(data?.invoiceNumber ? { invoiceNumber: data.invoiceNumber, invoiceIssuedAt: data.invoiceIssuedAt } : {}),
      ...(data?.reimbursementRefs ? { reimbursementRefs: data.reimbursementRefs } : {}),
    };
  } catch (_) {
    return withRefs;
  }
}

// Before an invoice prints: ask for the reimbursement reference of each eye
// (typed by the staff, printed under the lens line). Both are required — no
// references, no invoice number. Prefilled with what was saved last time.
// Resolves to { od, og }, or null when cancelled.
export function askReimbursementRefs(orderData) {
  const saved = orderData?.reimbursementRefs || orderData?.raw_data?.reimbursementRefs || {};
  return new Promise((resolve) => {
    const t = (k, d) => i18n.t(k, { defaultValue: d });
    const overlay = document.createElement('div');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9000;background:rgba(15,23,42,0.55);display:flex;align-items:center;justify-content:center;padding:16px;font-family:system-ui,-apple-system,Segoe UI,sans-serif';
    const field = (id, label, value) => `<label for="${id}" style="display:block;font-size:12px;font-weight:700;color:#475569;margin:12px 0 4px">${escapeHtml(label)}</label>`
      + `<input id="${id}" value="${escapeHtml(value || '')}" maxlength="60" autocomplete="off" style="width:100%;box-sizing:border-box;padding:9px 11px;border:1.5px solid #cbd5e1;border-radius:9px;font-size:15px" />`;
    // A two-pair sale asks OD and OG for each pair: four references.
    const twoPairs = !!pairsOf(orderData);
    const pairHead = (key) => `<div style="font-size:13px;font-weight:800;color:#3730a3;margin-top:14px">${escapeHtml(i18n.t(key))}</div>`;
    overlay.innerHTML = `<form style="background:#fff;border-radius:14px;padding:20px 22px;width:100%;max-width:380px;max-height:90vh;overflow:auto;box-shadow:0 20px 50px rgba(15,23,42,0.3)">
      <div style="font-size:16px;font-weight:800;color:#0f172a">${escapeHtml(t('invoiceRefs.title', 'Réf. remboursement'))}</div>
      <div style="font-size:13px;color:#64748b;margin-top:4px">${escapeHtml(twoPairs ? t('invoiceRefs.hintPairs', 'All four references are required to print the invoice.') : t('invoiceRefs.hint', 'Both references are required to print the invoice.'))}</div>
      ${twoPairs ? pairHead('pairs.pairFar') : ''}
      ${field('reimb-od', t('invoiceRefs.od', 'OD (right eye)'), saved.od)}
      ${field('reimb-og', t('invoiceRefs.og', 'OG (left eye)'), saved.og)}
      ${twoPairs ? `${pairHead('pairs.pairNear')}${field('reimb-near-od', t('invoiceRefs.od', 'OD (right eye)'), saved.nearOd)}${field('reimb-near-og', t('invoiceRefs.og', 'OG (left eye)'), saved.nearOg)}` : ''}
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:18px">
        <button type="button" data-act="cancel" style="padding:9px 14px;border-radius:9px;border:1.5px solid #cbd5e1;background:#fff;font-weight:600;cursor:pointer">${escapeHtml(t('invoiceRefs.cancel', 'Cancel'))}</button>
        <button type="submit" data-act="print" style="padding:9px 14px;border-radius:9px;border:none;background:#4f46e5;color:#fff;font-weight:700;cursor:pointer">${escapeHtml(t('invoiceRefs.print', 'Print invoice'))}</button>
      </div>
    </form>`;
    const close = (value) => { document.removeEventListener('keydown', onKey); overlay.remove(); resolve(value); };
    const onKey = (e) => { if (e.key === 'Escape') close(null); };
    overlay.querySelector('[data-act="cancel"]').addEventListener('click', () => close(null));
    overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(null); });
    const inputs = {
      od: overlay.querySelector('#reimb-od'),
      og: overlay.querySelector('#reimb-og'),
      ...(twoPairs ? { nearOd: overlay.querySelector('#reimb-near-od'), nearOg: overlay.querySelector('#reimb-near-og') } : {}),
    };
    const printBtn = overlay.querySelector('[data-act="print"]');
    const refresh = () => {
      const ready = Object.values(inputs).every((input) => input.value.trim() !== '');
      printBtn.disabled = !ready;
      printBtn.style.opacity = ready ? '1' : '0.45';
      printBtn.style.cursor = ready ? 'pointer' : 'not-allowed';
    };
    Object.values(inputs).forEach((input) => input.addEventListener('input', refresh));
    refresh();
    overlay.querySelector('form').addEventListener('submit', (e) => {
      e.preventDefault();
      const values = Object.fromEntries(Object.entries(inputs).map(([k, input]) => [k, input.value.trim()]));
      const missing = Object.keys(inputs).find((k) => !values[k]);
      if (missing) { inputs[missing].focus(); return; }
      close(values);
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);
    setTimeout(() => overlay.querySelector('#reimb-od')?.focus(), 0);
  });
}

// Wait (briefly) for a print window's images — the QR codes come over the
// network — so the page never prints with a blank QR.
function whenImagesLoaded(w, maxMs = 4000) {
  return new Promise((resolve) => {
    const pending = Array.from(w.document.images || []).filter((img) => !img.complete);
    if (pending.length === 0) return resolve();
    let left = pending.length;
    const done = () => { left -= 1; if (left <= 0) resolve(); };
    pending.forEach((img) => { img.addEventListener('load', done); img.addEventListener('error', done); });
    setTimeout(resolve, maxMs);
  });
}

async function resolvePrintBranding(orderData, branding) {
  const fresh = await fetchStoreBrandingForOrder(orderData);
  if (!fresh) return branding || {};
  const merged = { ...(branding || {}), ...fresh };
  try { localStorage.setItem('storeBranding', JSON.stringify({ ...RECEIPT_DEFAULTS, ...merged })); } catch (_) {}
  return merged;
}

const FONT_SIZE_PX        = { small: '0.78rem', medium: '0.9rem',  large: '1.05rem' };
const FOOTER_FONT_SIZE_PX = { small: '0.7rem',  medium: '0.78rem', large: '0.9rem'  };

function mergeLabels(custom, defaults) {
  return { ...defaults, ...(custom || {}) };
}

// Picks the strongest signal for a given label key: legacy flat field first
// (e.g. `remainingLabel`, `label_total`), then the nested `labels` object,
// then the built-in default. Lets old saves keep working unchanged while
// new edits in StoreBrandingSettings live under `labels`.
const LEGACY_LABEL_KEYS = {
  remaining: 'remainingLabel',
};
// Footer copy lives in the template config, where the built-in default has
// already been merged in — so "did the store customise this?" is answered by
// comparing against that default. Untouched text follows the UI language;
// text the store actually wrote is printed exactly as written.
function localizedTemplateText(value, defaultValue, key, vars) {
  const text = String(value ?? '').trim();
  if (text && text !== String(defaultValue ?? '').trim()) return text;
  return i18n.t(key, { defaultValue: text || defaultValue || '', ...(vars || {}) });
}

function pickLabel(tpl, defaults, key, ns) {
  const fallback = String(defaults[key] ?? '').trim();
  // Every store carries a full `labels` object — the branding page saves the
  // built-in defaults verbatim, and DEFAULT_RECEIPT_TEMPLATE embeds them too.
  // So "a value is stored" proves nothing; only a value that DIFFERS from the
  // English default is something the store actually wrote.
  const stored = [
    LEGACY_LABEL_KEYS[key] ? tpl?.[LEGACY_LABEL_KEYS[key]] : undefined,
    tpl?.[`label_${key}`],
    tpl?.labels?.[key],
  ].filter((v) => typeof v === 'string' && v.trim()).map((v) => v.trim());

  const custom = stored.find((v) => v !== fallback);
  if (custom) return custom;

  if (ns) {
    const translated = i18n.t(`${ns}.${key}`, { defaultValue: '' });
    if (translated) return translated;
  }
  return stored[0] || fallback;
}


/**
 * Builds a standalone HTML receipt for download or browser-print.
 *
 * @param {object} opts
 * @param {object}  opts.orderData
 * @param {object}  [opts.paymentData]
 * @param {object}  [opts.visitorData]
 * @param {string}  [opts.copyType]   'customer' | 'logistics' | 'invoice'
 * @param {object}  [opts.branding]   Store branding (from BrandingContext or API).
 *                                    Falls back to Optiva defaults when omitted.
 */
// ─── Algerian invoice (facture) ──────────────────────────────────────────────
//
// Rebuilt from scratch at the owner's request (2026-10-19). Always in French,
// the language of Algerian invoices whatever the app language. Layout:
//   logo + N° + date ....... FACTURE (centred) ....... order QR + reference
//   Vendeur: legal name, address, phone, email, NIF / NIS / RC
//   Client: NOM Prénom, phone, date of birth (age), order reference
//   Lines: Désignation | Qté | P.U. | Montant   (frames as "Monture médicale")
//   Total HT / TVA / Total TTC, paid, rest to pay, amount in words, stamp.
// The store's legal details and TVA rate live in its invoice template
// (Store branding → Invoice), saved with the rest of the branding.

const FR_UNITS = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
  'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const FR_TENS = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];

function frBelow100(n) {
  if (n < 20) return FR_UNITS[n];
  const t = Math.floor(n / 10);
  let u = n % 10;
  if (t === 7 || t === 9) u += 10;               // 70-79 / 90-99 count on from 10
  const tens = FR_TENS[t];
  if (u === 0) return t === 8 ? 'quatre-vingts' : tens;
  if ((u === 1 || u === 11) && t !== 8 && t !== 9) return `${tens} et ${FR_UNITS[u]}`;
  return `${tens}-${FR_UNITS[u]}`;
}

function frBelow1000(n) {
  const h = Math.floor(n / 100);
  const r = n % 100;
  const head = h === 0 ? '' : h === 1 ? 'cent' : `${FR_UNITS[h]} cent${r === 0 ? 's' : ''}`;
  if (r === 0) return head;
  return head ? `${head} ${frBelow100(r)}` : frBelow100(r);
}

// 19500 → "dix-neuf mille cinq cents"
export function numberToFrenchWords(value) {
  let n = Math.floor(Math.abs(Number(value) || 0));
  if (n === 0) return 'zéro';
  const parts = [];
  const scales = [[1e9, 'milliard'], [1e6, 'million'], [1e3, 'mille']];
  for (const [size, name] of scales) {
    const q = Math.floor(n / size);
    if (q) {
      if (name === 'mille') parts.push(q === 1 ? 'mille' : `${frBelow1000(q).replace(/cents$/, 'cent').replace(/vingts$/, 'vingt')} mille`);
      else parts.push(`${frBelow1000(q)} ${name}${q > 1 ? 's' : ''}`);
      n %= size;
    }
  }
  if (n) parts.push(frBelow1000(n));
  return parts.join(' ');
}

function amountInWordsDzd(amount) {
  const total = Math.round((Number(amount) || 0) * 100);
  const dinars = Math.floor(total / 100);
  const centimes = total % 100;
  let text = `${numberToFrenchWords(dinars)} dinar${dinars > 1 ? 's' : ''} algérien${dinars > 1 ? 's' : ''}`;
  if (centimes) text += ` et ${numberToFrenchWords(centimes)} centime${centimes > 1 ? 's' : ''}`;
  return text;
}

// 19500 → "19 500,00"
function fmtFr(n) {
  const v = Number(n) || 0;
  const [int, dec] = Math.abs(v).toFixed(2).split('.');
  return `${v < 0 ? '-' : ''}${int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${dec}`;
}

function frDate(d) {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return `${String(x.getDate()).padStart(2, '0')}/${String(x.getMonth() + 1).padStart(2, '0')}/${x.getFullYear()}`;
}

const FR_PAYMENT_METHODS = {
  cash: 'Espèces', especes: 'Espèces', card: 'Carte bancaire', cib: 'Carte CIB', edahabia: 'Carte Edahabia',
  cheque: 'Chèque', check: 'Chèque', transfer: 'Virement', virement: 'Virement', ccp: 'CCP', baridimob: 'BaridiMob',
};

function invoiceLineLabel(item) {
  const kind = item.type || item.item_type;
  const name = itemDisplayName(item);
  if (kind === 'frame') return `Monture médicale ${name}`;
  if (kind === 'lens') return `Verre ${name}`;
  if (kind === 'contact_lens' || kind === 'contact') return `Lentilles de contact ${name}`;
  return name;
}

export function buildAlgerianInvoiceHtml({ orderData, paymentData = {}, visitorData = {}, branding = {} }) {
  const b = { ...RECEIPT_DEFAULTS, ...branding };
  const inv = b.invoiceTemplate || {};
  const esc = escapeHtml;

  // ── Invoice identity ──
  const raw = orderData.raw_data || {};
  const invoiceNumber = orderData.invoiceNumber || raw.invoiceNumber || '';
  const issuedAt = orderData.invoiceIssuedAt || raw.invoiceIssuedAt || Date.now();
  const orderRef = orderData.orderNumber || '';
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=2&data=${encodeURIComponent(orderRef)}`;

  // ── Seller (store) ──
  const store = (orderData.store && typeof orderData.store === 'object') ? orderData.store : {};
  const storeAddress = [
    store.address?.street || store.addressStreet || store.address_street || (typeof store.address === 'string' ? store.address : '') || b.street || '',
    store.address?.city || store.addressCity || b.city || '',
  ].filter(Boolean).join(', ');
  const sellerName = inv.legalName || b.storeName || store.name || '';
  const storeTradeName = store.name && store.name !== sellerName ? store.name : '';
  const phone = store.phone || b.phone || '';
  const email = inv.email || store.email || b.email || '';
  const legal = [['NIF', inv.nif], ['NIS', inv.nis], ['Agrément DDS', inv.agrementDds]];

  // ── Client ──
  const visitor = orderData.visitor || {};
  const firstName = visitor.firstName || visitorData.firstName || '';
  const lastName = visitor.lastName || visitorData.lastName || '';
  const clientName = [String(lastName).toUpperCase(), firstName].filter(Boolean).join(' ')
    || fullName(visitorData) || visitorData.name || '—';
  const clientPhone = visitor.phone || visitor.phoneNumber || visitorData.phone || visitorData.phoneNumber || '';
  const birthday = visitor.birthday || visitor.patientBirthday || visitorData.birthday || visitorData.patientBirthday || visitorData.patientBirthdayIso || '';
  let age = visitor.age ?? visitorData.age ?? visitorData.patientAge ?? null;
  if ((age == null || age === '') && birthday) {
    const bd = new Date(birthday);
    if (!Number.isNaN(bd.getTime())) {
      const now = new Date(issuedAt);
      age = now.getFullYear() - bd.getFullYear() - ((now.getMonth() < bd.getMonth() || (now.getMonth() === bd.getMonth() && now.getDate() < bd.getDate())) ? 1 : 0);
    }
  }
  const birthLine = birthday && frDate(birthday)
    ? `Né(e) le : ${esc(frDate(birthday))}${age != null && age !== '' ? ` (${esc(String(age))} ans)` : ''}`
    : (age != null && age !== '' ? `Âge : ${esc(String(age))} ans` : '');

  // ── Lines ── (a client's own frame is not something the shop sold)
  // Lenses: an order line is priced for the pair, but the invoice lists each
  // eye on its own line (OD, OG) with half the price and that eye's
  // reimbursement reference, typed before printing. Totals are unchanged —
  // an odd centime goes to OD.
  const refs = orderData.reimbursementRefs || raw.reimbursementRefs || {};
  const refLine = (ref) => (ref ? `Réf. remboursement : ${ref}` : '');
  const lines = collectItems(orderData)
    .filter((item) => !((item.type || item.item_type) === 'frame' && isOwnFrameName(item.name)))
    .flatMap((item) => {
      const qty = Number(item.quantity ?? 1) || 1;
      const unitRaw = item.unitPrice ?? item.unit_price;
      const totalRaw = item.totalPrice ?? item.total_price;
      const unit = unitRaw != null ? Number(unitRaw) : (totalRaw != null ? Number(totalRaw) / qty : 0);
      const amount = totalRaw != null ? Number(totalRaw) : unit * qty;
      if ((item.type || item.item_type) !== 'lens') {
        return [{ label: invoiceLineLabel(item), sub: '', qty, unit, amount }];
      }
      const odAmount = Math.round((amount * 100) / 2) / 100;
      const ogAmount = Math.round((amount - odAmount) * 100) / 100;
      // Pair 2 (close up) of a two-pair sale has its own references.
      const near = item.specifications?.visionPair === 'near';
      return [
        { label: `${invoiceLineLabel(item)} — OD`, sub: refLine(near ? refs.nearOd : refs.od), qty, unit: odAmount / qty, amount: odAmount },
        { label: `${invoiceLineLabel(item)} — OG`, sub: refLine(near ? refs.nearOg : refs.og), qty, unit: ogAmount / qty, amount: ogAmount },
      ];
    });
  const linesSum = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  // ── Totals ── (shop prices are TTC; HT and TVA are derived from them)
  const ttc = Number(orderData.total ?? orderData.totalAmount ?? linesSum) || 0;
  const discount = linesSum > ttc + 0.004 ? linesSum - ttc : 0;
  const rateRaw = inv.tvaRate === undefined || inv.tvaRate === null || inv.tvaRate === '' ? 19 : Number(inv.tvaRate);
  const tvaRate = Number.isFinite(rateRaw) && rateRaw > 0 ? rateRaw : 0;
  const ht = tvaRate ? ttc / (1 + tvaRate / 100) : ttc;
  const tva = ttc - ht;
  const pay = orderData.payment || {};
  const paid = Number(paymentData.deposit ?? pay.amount ?? 0) || 0;
  const rest = paymentData.remaining != null ? Number(paymentData.remaining) : Math.max(0, ttc - paid);
  const methodKey = String(paymentData.method || pay.method || '').toLowerCase().replace(/[^a-z]/g, '');
  const method = FR_PAYMENT_METHODS[methodKey] || (paymentData.method || pay.method || '');
  const primary = b.primaryColor || '#0f172a';

  const row = (label, value, extra = '') => `<div class="tot-row ${extra}"><span>${label}</span><span>${value}</span></div>`;

  return `<!DOCTYPE html>
<html lang="fr" dir="ltr">
<head>
<meta charset="utf-8"/>
<title>Facture ${esc(invoiceNumber || orderRef)}</title>
<style>
  @page { size: A4 portrait; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, -apple-system, "Segoe UI", sans-serif; color: #0f172a; font-size: 12px; line-height: 1.45; background: #fff; }
  .sheet { width: 186mm; margin: 0 auto; }
  .top { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid ${primary}; }
  .logo { max-height: 48px; max-width: 160px; object-fit: contain; display: block; margin-bottom: 6px; }
  .title { font-size: 26px; font-weight: 800; letter-spacing: 0.06em; color: #000; text-align: center; }
  .muted { color: #64748b; }
  .qr-side { display: flex; align-items: center; justify-content: flex-end; gap: 10px; text-align: right; }
  .qr { width: 78px; height: 78px; }
  .lbl { font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase; color: #94a3b8; font-weight: 700; }
  .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-top: 14px; }
  .box { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 12px; }
  .box .name { font-weight: 800; font-size: 14px; }
  .legal { display: grid; grid-template-columns: 1fr 1fr; gap: 1px 12px; margin-top: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 16px; }
  th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: #475569; font-weight: 700; text-align: left; border-bottom: 1.5px solid #0f172a; padding: 6px 4px; }
  td { padding: 7px 4px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  .r { text-align: right; white-space: nowrap; }
  .sub { font-size: 10.5px; color: #64748b; margin-top: 2px; }
  .totals { display: flex; justify-content: flex-end; margin-top: 12px; }
  .totals .inner { min-width: 260px; }
  .tot-row { display: flex; justify-content: space-between; gap: 16px; padding: 2px 0; }
  .tot-row.ttc { font-weight: 800; font-size: 14px; border-top: 1.5px solid #0f172a; margin-top: 4px; padding-top: 5px; }
  .tot-row.rest { color: #b91c1c; font-weight: 700; }
  .words { margin-top: 14px; font-style: italic; }
  .foot { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 22px; gap: 16px; }
  .stamp { width: 190px; height: 80px; border: 1.5px dashed #94a3b8; border-radius: 8px; display: flex; justify-content: center; padding-top: 5px; }
  @media print { .sheet { width: auto; } }
</style>
</head>
<body>
<div class="sheet">
  <div class="top">
    <div>
      ${b.logo ? `<img class="logo" src="${esc(b.logo)}" alt="" />` : ''}
      <div>N° <strong>${esc(invoiceNumber || '—')}</strong></div>
      <div class="muted">Date : ${esc(frDate(issuedAt))}</div>
    </div>
    <div class="title">FACTURE</div>
    <div class="qr-side">
      <div>
        <div class="lbl">Réf. commande</div>
        <div><strong>${esc(orderRef || '—')}</strong></div>
      </div>
      ${orderRef ? `<img class="qr" src="${qrSrc}" alt="QR ${esc(orderRef)}" />` : ''}
    </div>
  </div>

  <div class="parties">
    <div class="box">
      <div class="lbl">Vendeur</div>
      <div class="name">${esc(sellerName)}${storeTradeName ? ` — ${esc(storeTradeName)}` : ''}</div>
      ${storeAddress ? `<div>${esc(storeAddress)}</div>` : ''}
      ${phone || email ? `<div>${[phone ? `Tél : ${esc(phone)}` : '', email ? esc(email) : ''].filter(Boolean).join(' · ')}</div>` : ''}
      <div class="legal">
        ${legal.map(([k, v]) => `<div><span class="muted">${k} :</span> ${esc(v || '—')}</div>`).join('')}
      </div>
    </div>
    <div class="box">
      <div class="lbl">Client</div>
      <div class="name">${esc(clientName)}</div>
      ${clientPhone ? `<div>Tél : ${esc(clientPhone)}</div>` : ''}
      ${birthLine ? `<div>${birthLine}</div>` : ''}
      <div class="muted" style="margin-top:4px">Réf. commande : ${esc(orderRef || '—')}</div>
    </div>
  </div>

  <table>
    <thead><tr><th>Désignation</th><th class="r">Qté</th><th class="r">P.U. (DA)</th><th class="r">Montant (DA)</th></tr></thead>
    <tbody>
      ${lines.length ? lines.map((l) => `<tr>
        <td>${esc(l.label)}${l.sub ? `<div class="sub">${esc(l.sub)}</div>` : ''}</td>
        <td class="r">${esc(String(l.qty))}</td>
        <td class="r">${fmtFr(l.unit)}</td>
        <td class="r">${fmtFr(l.amount)}</td>
      </tr>`).join('') : '<tr><td colspan="4" class="muted">—</td></tr>'}
    </tbody>
  </table>

  <div class="totals"><div class="inner">
    ${discount ? row('<span class="muted">Remise</span>', `- ${fmtFr(discount)}`) : ''}
    ${tvaRate
      ? row('<span class="muted">Total HT</span>', fmtFr(ht)) + row(`<span class="muted">TVA ${esc(String(tvaRate).replace('.', ','))} %</span>`, fmtFr(tva))
      : row('<span class="muted">TVA</span>', 'non applicable')}
    ${row('Total TTC', `${fmtFr(ttc)} DA`, 'ttc')}
    ${row('<span class="muted">Versé</span>', fmtFr(paid))}
    ${row('Reste à payer', `${fmtFr(rest)} DA`, 'rest')}
  </div></div>

  <div class="words">Arrêtée la présente facture à la somme de : ${esc(amountInWordsDzd(ttc))}.</div>

  <div class="foot">
    <div class="muted">${method ? `Mode de paiement : ${esc(method)}` : ''}</div>
    <div class="stamp"><span class="lbl">Cachet et signature</span></div>
  </div>
</div>
</body>
</html>`;
}

export function buildReceiptHtml({ orderData, paymentData = {}, visitorData = {}, copyType = 'customer', branding = {} }) {
  // The invoice is its own document now (Algerian facture, see above).
  if (copyType === 'invoice') return buildAlgerianInvoiceHtml({ orderData, paymentData, visitorData, branding });

  const b      = { ...RECEIPT_DEFAULTS, ...branding };
  const invTpl = { ...DEFAULT_INVOICE_TEMPLATE, ...(b.invoiceTemplate || {}) };
  const recTpl = { ...DEFAULT_RECEIPT_TEMPLATE, ...(b.receiptTemplate || {}) };
  const invL   = mergeLabels(invTpl.labels, DEFAULT_INVOICE_LABELS);
  const recL   = mergeLabels(recTpl.labels, DEFAULT_RECEIPT_LABELS);

  const isLogistics = copyType === 'logistics';
  const isInvoice   = copyType === 'invoice';
  const tpl         = isInvoice ? invTpl : recTpl;
  const defLabels   = isInvoice ? DEFAULT_INVOICE_LABELS : DEFAULT_RECEIPT_LABELS;
  const labelNs     = isInvoice ? 'invoiceLabels' : 'receiptLabels';
  const L           = (key) => pickLabel(tpl, defLabels, key, labelNs);

  const orderNumber = escapeHtml(orderData.orderNumber || '—');
  const rawDate     = orderData.orderDate || orderData.createdAt || Date.now();
  const dateStr     = escapeHtml(new Date(rawDate).toLocaleString());

  const visitor = orderData.visitor;
  let customerLine = fullName(visitor) || fullName(visitorData) || visitorData.name || '—';
  if (customerLine === '—' && (visitorData.gender || visitorData.ageGroup)) {
    customerLine = [visitorData.gender, visitorData.ageGroup].filter(Boolean).join(' · ');
  }
  customerLine = escapeHtml(customerLine);

  const customerBirthday = visitor?.birthday || visitor?.patientBirthday || visitorData.birthday || visitorData.patientBirthday || visitorData.patientBirthdayIso;
  const customerPhone    = visitor?.phone || visitor?.phoneNumber || visitorData.phone || visitorData.phoneNumber || '';
  // Receipts identify the salesperson by employee ID, not by name (owner's
  // request); the name is only a fallback for profiles with no ID set.
  const salesperson      = orderData.salesPerson?.employeeId || fullName(orderData.salesPerson) || '—';
  const technicianLine   = fullName(orderData.technician)  || orderData.technician?.employeeId  || '';
  const frameDisplayText = frameSummaryDisplayName(orderData);
  const lensItem         = findItem(orderData, 'lens');

  const items = collectItems(orderData);

  const m         = orderData.measurements || {};
  const measParts = [];
  if (m.mountingHeightOD != null || m.mountingHeightOG != null) {
    measParts.push(`Hauteur de montage: OD ${m.mountingHeightOD ?? '—'} / OG ${m.mountingHeightOG ?? '—'} mm`);
  } else if (m.mountingHeight != null) measParts.push(`Hauteur de montage: ${m.mountingHeight} mm`);
  if (m.pdOD != null) measParts.push(`DP OD: ${m.pdOD} mm`);
  if (m.pdOG != null) measParts.push(`DP OG: ${m.pdOG} mm`);

  const pay      = orderData.payment || {};
  const deposit  = paymentData.deposit ?? pay.amount ?? 0;
  const remaining =
    paymentData.remaining != null
      ? paymentData.remaining
      : Math.max(0, Number(orderData.total || 0) - Number(deposit || 0));

  const store     = orderData.store;
  const storeLine =
    store && typeof store === 'object'
      ? escapeHtml(store.name || store.storeCode || '')
      : '';

  // ── Header / badge labels (now editable) ───────────────────────────────────
  const pageTitle  = isLogistics ? 'Equipment copy' : L('pageTitle');
  const heading    = isLogistics ? 'Equipment copy' : L('heading');
  const badgeLabel = isLogistics ? 'Equipment copy — attach to frame' : L('badge');
  const badgeBg    = isLogistics ? '#e0f2fe' : isInvoice ? '#eef2ff' : '#dcfce7';
  const badgeColor = isLogistics ? '#075985' : isInvoice ? '#4338ca' : '#047857';

  // ── QR ─────────────────────────────────────────────────────────────────────
  const wantsQr  = isLogistics ? true
                 : isInvoice    ? invTpl.showQR !== false
                 :                recTpl.showQR !== false;
  const qrCaption = (isInvoice ? invL.qrCaption : recL.qrCaption) || orderData.orderNumber || '';
  const qr       = wantsQr ? buildQrImage(orderData.orderNumber || '', qrCaption) : '';

  // ── Logo markup ────────────────────────────────────────────────────────────
  const wantsLogo = isInvoice
    ? invTpl.showLogo !== false
    : recTpl.showLogo !== false;
  const logoHtml = (b.logo && wantsLogo)
    ? `<img src="${escapeHtml(b.logo.startsWith('http') ? b.logo : '/' + b.logo)}" alt="${escapeHtml(b.storeName)} logo" class="store-logo" />`
    : '';

  // ── Contact footer line — built from per-template toggles when available ──
  const ftrPhone   = isInvoice ? invTpl.showFooterPhone   : recTpl.showPhone;
  const ftrAddress = isInvoice ? invTpl.showFooterAddress : recTpl.showAddress;
  const contactParts = [
    b.storeName,
    ftrAddress ? b.city  : '',
    ftrPhone   ? b.phone : '',
    b.email,
  ].filter(Boolean);
  const contactLine = escapeHtml(contactParts.join(' · '));

  // ── Header alignment for invoice copies ────────────────────────────────────
  const headerAlign = isInvoice
    ? (invTpl.headerLogoPosition === 'center' ? 'center'
       : invTpl.headerLogoPosition === 'right' ? 'flex-end'
       : 'flex-start')
    : 'flex-start';
  const headerTextAlign = isInvoice && invTpl.headerLogoPosition === 'center' ? 'center' : 'left';

  // ── Per-template font sizing ───────────────────────────────────────────────
  const bodyFontSize = isInvoice
    ? (FONT_SIZE_PX[invTpl.footerFontSize] || FONT_SIZE_PX.medium)
    : (FONT_SIZE_PX[recTpl.fontSize] || FONT_SIZE_PX.medium);
  const footerFontSize = isInvoice
    ? (FOOTER_FONT_SIZE_PX[invTpl.footerFontSize] || FOOTER_FONT_SIZE_PX.medium)
    : (FOOTER_FONT_SIZE_PX[recTpl.fontSize] || FOOTER_FONT_SIZE_PX.medium);

  // ── Conditional info-grid cells — every tile is now toggle-able. ───────────
  // The mapping below mirrors the toggles exposed in StoreBrandingSettings's
  // Invoice & Receipt Layout tabs. Whenever a flag is undefined we keep the
  // historical default (true) for that copy type so older saves keep working.
  let showInvoiceNum      = isInvoice ? invTpl.showInvoiceNumber : recTpl.showOrderNumber;
  let showDateField       = isInvoice ? invTpl.showDate          : recTpl.showDate;
  const showSalesField    = isInvoice ? invTpl.showSalesPerson   : recTpl.showSalesPerson;
  const showStoreField    = isInvoice ? invTpl.showStore         : recTpl.showStore;
  let showClientName      = isInvoice ? invTpl.showClientName    : recTpl.showCustomer;
  let showClientPh        = isInvoice ? invTpl.showClientPhone   : recTpl.showCustomerPhone;
  const showCustomerBday  = isInvoice ? invTpl.showClientBirthday : recTpl.showCustomerBirthday;
  let showFrameField      = isInvoice ? invTpl.showFrame         : recTpl.showFrame;
  let showLensField       = isInvoice ? invTpl.showLens          : recTpl.showLens;
  let showTimeDeliver     = isInvoice ? invTpl.showTimeToDeliver : recTpl.showTimeToDeliver;
  let showStatusField     = isInvoice ? invTpl.showStatus        : recTpl.showStatus;
  const showTechField     = isInvoice && invTpl.showTechnician;
  const showDueDateField  = isInvoice && invTpl.showDueDate;
  const showGenderAge     = isInvoice && invTpl.showClientGenderAge && (visitorData.gender || visitorData.ageGroup);
  const showVisitNumber   = isInvoice && invTpl.showVisitNumber  && orderData.visitNumber != null;

  // The equipment copy is physically attached to the frame, so it must always
  // carry the fields that let staff identify exactly which frame + lenses this
  // order is for — regardless of the store's receipt-layout toggles.
  if (isLogistics) {
    showInvoiceNum = true;
    showDateField  = true;
    showClientName = true;
    showClientPh   = true;
    showFrameField = true;
    showLensField  = true;
    showTimeDeliver = true;
    showStatusField = true;
  }

  const tile = (label, value) =>
    `<div class="info"><span class="label">${escapeHtml(label)}</span><span class="value">${value}</span></div>`;

  const infoTiles = [
    showInvoiceNum    && tile(isInvoice ? L('invoiceNumber') : L('orderNumber'), orderNumber),
    showDueDateField  && tile(L('dueDate'), escapeHtml(formatDate(orderData.dueDate || new Date(Date.now() + 7 * 86400000)))),
    showDateField     && tile(L('date'),    dateStr),
    showStoreField    && tile(L('store'),   storeLine || escapeHtml(b.storeName)),
    showClientName    && tile(L('customer'), customerLine),
    showClientPh      && tile(L('phone'),    escapeHtml(customerPhone || '—')),
    showCustomerBday  && tile(L('birthday'), escapeHtml(formatDate(customerBirthday))),
    showGenderAge     && tile(L('genderAge'), escapeHtml([visitorData.gender, visitorData.ageGroup].filter(Boolean).join(' · '))),
    showVisitNumber   && tile(L('visitNumber'), escapeHtml(String(orderData.visitNumber))),
    showSalesField    && tile(L('salesPerson'), escapeHtml(salesperson)),
    showTechField     && tile(L('technician'),  escapeHtml(technicianLine || '—')),
    showFrameField    && tile(L('frame'), escapeHtml(frameDisplayText)),
    showLensField     && tile(L('lens'),  escapeHtml(collectItems(orderData).filter((item) => (item.type || item.item_type) === 'lens').map(itemDisplayName).join(' + ') || itemDisplayName(lensItem))),
    (() => {
      // Two-pair sale: each pair's correction, named.
      const salePairsInv = pairsOf(orderData);
      if (salePairsInv) {
        const value = salePairsInv.map((pr) => {
          const l = getPairCorrectionLines(orderData, pr.visionPair);
          return [`<b>${escapeHtml(tr(pr.visionPair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar'))}</b>`,
            l.od ? `OD: ${escapeHtml(l.od)}` : '', l.os ? `OS: ${escapeHtml(l.os)}` : ''].filter(Boolean).join('<br>');
        }).join('<br>');
        return `<div class="info" style="grid-column:span 2"><span class="label">Correction</span><span class="value">${value}</span></div>`;
      }
      const { od, os } = getCorrectionLines(orderData); return (od || os) ? `<div class="info" style="grid-column:span 2"><span class="label">Correction</span><span class="value">${od ? `OD: ${escapeHtml(od)}` : ''}${od && os ? '<br>' : ''}${os ? `OS: ${escapeHtml(os)}` : ''}</span></div>` : false;
    })(),
    showTimeDeliver   && tile(L('timeToDeliver'), escapeHtml(estimatedDelivery(orderData))),
    showStatusField   && tile(L('status'), escapeHtml(orderData.status || '—')),
  ].filter(Boolean).join('');

  // ── Items table — for invoices honour the column toggles ───────────────────
  const showItemsTable = isLogistics ? true : isInvoice ? true : recTpl.showItemsTable !== false;
  let showLensTypeCol  = isInvoice ? invTpl.showColLensType   : recTpl.showColLensType;
  let showSphCylCol    = isInvoice ? invTpl.showColSphCyl     : recTpl.showColSphCyl;
  let showUnitPriceCol = isInvoice ? invTpl.showColUnitPrice : recTpl.showColUnitPrice;
  let showQtyCol       = isInvoice ? invTpl.showColQty       : recTpl.showColQty;
  let showTotalCol     = isInvoice ? invTpl.showColTotal     : recTpl.showColTotal;

  // Equipment copy: show what identifies the lenses (type, sph/cyl, qty), never prices.
  if (isLogistics) {
    showLensTypeCol = true;
    showSphCylCol   = true;
    showQtyCol      = true;
    showUnitPriceCol = false;
    showTotalCol     = false;
  }

  const tableHead = `<tr>
    <th>${escapeHtml(L('colDescription'))}</th>
    ${showLensTypeCol  ? `<th>${escapeHtml(L('colLensType'))}</th>`  : ''}
    ${showSphCylCol    ? `<th>${escapeHtml(L('colSphCyl'))}</th>`    : ''}
    ${showQtyCol       ? `<th>${escapeHtml(L('colQty'))}</th>`       : ''}
    ${showUnitPriceCol ? `<th style="text-align:right">${escapeHtml(L('colUnitPrice'))}</th>` : ''}
    ${showTotalCol     ? `<th style="text-align:right">${escapeHtml(L('colTotal'))}</th>`     : ''}
  </tr>`;

  const rowsTpl = items
    .map((item) => {
      const isLens = item.type === 'lens' || item.item_type === 'lens';
      const commercial = item.specifications?.commercialName || item.commercialName;
      const name  = escapeHtml((isLens && commercial) || item.name || item.type || 'Item');
      const brand = item.brand ? ` ${escapeHtml(item.brand)}` : '';
      const qty   = item.quantity ?? 1;
      const unit  = Number(item.unitPrice ?? 0);
      const line  = Number(item.totalPrice ?? item.unitPrice ?? 0);
      const lensType = item.specifications?.lensType || (item.type === 'lens' ? (item.brand || '—') : '—');
      const sph = item.specifications?.sph ?? '—';
      const cyl = item.specifications?.cyl ?? '—';
      return `<tr>
        <td>${name}${brand}</td>
        ${showLensTypeCol  ? `<td>${escapeHtml(String(lensType))}</td>` : ''}
        ${showSphCylCol    ? `<td>${escapeHtml(String(sph))} / ${escapeHtml(String(cyl))}</td>` : ''}
        ${showQtyCol       ? `<td>${escapeHtml(String(qty))}</td>` : ''}
        ${showUnitPriceCol ? `<td style="text-align:right">${escapeHtml(formatMoneyDzd(unit))}</td>` : ''}
        ${showTotalCol     ? `<td style="text-align:right">${escapeHtml(formatMoneyDzd(line))}</td>` : ''}
      </tr>`;
    })
    .join('');

  // Optional Rx detail row (invoice template extra, or receipt body toggle)
  const wantsRx = isInvoice ? invTpl.showLensPrescription : recTpl.showPrescriptionDetails;
  const rxRow = (wantsRx && (lensItem || measParts.length))
    ? (() => {
        const colCount = 1 + [showLensTypeCol, showSphCylCol, showQtyCol, showUnitPriceCol, showTotalCol].filter(Boolean).length;
        const rxBits = [];
        if (lensItem?.specifications?.sph != null)  rxBits.push(`SPH ${lensItem.specifications.sph}`);
        if (lensItem?.specifications?.cyl != null)  rxBits.push(`CYL ${lensItem.specifications.cyl}`);
        if (lensItem?.specifications?.axis != null) rxBits.push(`AX ${lensItem.specifications.axis}°`);
        if (!rxBits.length) rxBits.push(...measParts);
        return `<tr><td colspan="${colCount}" style="color:#64748b;font-style:italic;font-size:0.85em;">${escapeHtml(L('rxPrefix'))}: ${escapeHtml(rxBits.join(' · ') || '—')}</td></tr>`;
      })()
    : '';

  // ── Totals — invoice & receipt toggles unified ─────────────────────────────
  const showSubtotal      = isInvoice ? invTpl.showSubtotal         : recTpl.showSubtotal;
  const showDiscount      = isInvoice ? invTpl.showDiscount         : recTpl.showDiscount;
  const showAmountPaid    = isInvoice ? invTpl.showAmountPaid       : recTpl.showAmountPaid;
  const showRemaining     = isInvoice ? invTpl.showRemainingBalance : recTpl.showRemainingBalance;
  const showPaymentLine   = !isInvoice && recTpl.showPayment;
  const showTotalLine     = isInvoice ? true : recTpl.showTotal !== false;

  const totalsHtml = isLogistics ? '' : `
    <div class="totals">
      ${showSubtotal && orderData.subtotal != null
        ? `<div><span>${escapeHtml(L('subtotal'))}</span><span>${escapeHtml(formatMoneyDzd(orderData.subtotal))}</span></div>` : ''}
      ${(orderData.tax != null && Number(orderData.tax) !== 0)
        ? `<div><span>Tax</span><span>${escapeHtml(formatMoneyDzd(orderData.tax))}</span></div>` : ''}
      ${showDiscount && orderData.discount
        ? `<div><span>${escapeHtml(L('discount'))}</span><span>${escapeHtml(formatMoneyDzd(orderData.discount))}</span></div>` : ''}
      ${showTotalLine
        ? `<div class="strong"><span>${escapeHtml(L('total'))}</span><span>${escapeHtml(formatMoneyDzd(orderData.total))}</span></div>` : ''}
      ${showPaymentLine
        ? `<div><span>${escapeHtml(L('payment'))} (${escapeHtml(paymentMethodLabel(pay.method || 'cash'))})</span><span>${escapeHtml(paymentStatusLabel(pay.status) || '—')}</span></div>`
        : ''}
      ${showAmountPaid ? `<div><span>${escapeHtml(L('amountPaid'))}</span><span>${escapeHtml(formatMoneyDzd(deposit))}</span></div>` : ''}
      ${showRemaining  ? `<div><span class="accent">${escapeHtml(L('remaining'))}</span><span class="accent">${escapeHtml(formatMoneyDzd(remaining))}</span></div>` : ''}
    </div>
  `;

  // ── Footer custom message + return-policy (receipt only) ───────────────────
  const tplFooterMsg = localizedTemplateText(
    isInvoice ? invTpl.footerMessage : recTpl.footerMessage,
    isInvoice ? DEFAULT_INVOICE_TEMPLATE.footerMessage : DEFAULT_RECEIPT_TEMPLATE.footerMessage,
    isInvoice ? 'receiptFooter.invoiceMessage' : 'receiptFooter.message',
  ).trim();
  const returnPolicyText = localizedTemplateText(
    recTpl.returnPolicyText, DEFAULT_RECEIPT_TEMPLATE.returnPolicyText, 'receiptFooter.returnPolicy',
  ).trim();
  const returnPolicyHtml = (!isInvoice && recTpl.showReturnPolicy && returnPolicyText)
    ? `<div style="margin-top:6px;font-size:0.85em;color:#94a3b8;">${escapeHtml(returnPolicyText)}</div>`
    : '';

  // ── Default footer copy (when template has no custom message) ─────────────
  const defaultFooterCopy = isLogistics
    ? 'Logistics copy: keep this with the physical order/package until store handoff.'
    : isInvoice
      ? 'Invoice for accounting. Amounts in DZD. Balance due reflects current payment recorded in the system.'
      : 'Thank you for your order. Keep this receipt for your records.<br/>Track your order using your order number in store or on the Track Order page.';
  const footerCopy = tplFooterMsg ? escapeHtml(tplFooterMsg) : defaultFooterCopy;

  // ── Optional invoice website line (admin-customisable) ────────────────────
  const websiteText = (invTpl.footerWebsite || '').trim() || 'www.optiva.dz';
  const websiteHtml = (isInvoice && invTpl.showFooterWebsite)
    ? `<br/><span style="color:#aaa">${escapeHtml(websiteText)}</span>`
    : '';

  if (!isInvoice && !isLogistics) {
    const receiptLogo = (b.logo && recTpl.showLogo !== false)
      ? `<img class="r-logo-img" src="${escapeHtml(b.logo.startsWith('http') ? b.logo : '/' + b.logo)}" alt="${escapeHtml(b.storeName)} logo" />`
      : '';

    // Date and time sit on separate lines under the salesperson, so split the
    // combined timestamp rather than reusing the one-line dateStr.
    const saleDay  = new Date(rawDate).toLocaleDateString();
    const saleHour = new Date(rawDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const storeMeta = [
      recTpl.showPhone   !== false ? b.phone : '',
      recTpl.showEmail   !== false ? b.email : '',
    ].filter(Boolean).join(' · ');
    const storeAddress = recTpl.showAddress !== false ? (b.city || '') : '';

    const { od: corrOd, os: corrOs } = getCorrectionLines(orderData);
    const hasCorrection = recTpl.showCorrection !== false && (corrOd || corrOs);
    // Two-pair sale: both order numbers, and each pair's correction under its name.
    const salePairs = pairsOf(orderData);
    const ordDisplay = orderNumber;
    const rxLines = salePairs
      ? salePairs.map((pr) => {
        const lines = getPairCorrectionLines(orderData, pr.visionPair);
        return `<div class="rx-line"><em>${escapeHtml(tr(pr.visionPair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar'))}</em></div>`
          + (lines.od ? `<div class="rx-line"><em>OD</em>${escapeHtml(lines.od)}</div>` : '')
          + (lines.os ? `<div class="rx-line"><em>OS</em>${escapeHtml(lines.os)}</div>` : '');
      }).join('')
      : `${corrOd ? `<div class="rx-line"><em>OD</em>${escapeHtml(corrOd)}</div>` : ''}${corrOs ? `<div class="rx-line"><em>OS</em>${escapeHtml(corrOs)}</div>` : ''}`;

    const moneyRow = (label, value, cls = '') =>
      `<div class="money-row ${cls}"><span class="k">${escapeHtml(label)}</span><span class="v">${escapeHtml(value)}</span></div>`;

    // Expected delivery as the real pickup date (working days, Fridays
    // skipped — the same date the equipment passport prints), with the lead
    // time underneath. No lead time on any line → the generic window text.
    const deliveryDays = totalDeliveryDays(orderData);
    const deliveryDate = expectedDeliveryDate(orderData);
    const deliveryHtml = deliveryDate
      ? `<span class="val">${escapeHtml(deliveryDate.toLocaleDateString(i18n.language || undefined, { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }))}</span>`
        + (deliveryDays ? `<span class="sub">${escapeHtml(tr('passport.daysCount', { count: deliveryDays }))}</span>` : '')
      : `<span class="val">${escapeHtml(estimatedDelivery(orderData))}</span>`;

    // What was sold, one priced line per frame / lens. A client's own frame
    // is not something the shop sold, so it is left off entirely (owner's
    // rule). Every frame gets its own line — spares included — instead of
    // the old single name with a "(+1)".
    const soldRows = items
      .filter((item) => {
        const kind = item.type || item.item_type;
        if (kind === 'frame') return recTpl.showFrame !== false && !isOwnFrameName(item.name);
        if (kind === 'lens') return recTpl.showLens !== false;
        return false;
      })
      .map((item) => {
        const isLens = (item.type || item.item_type) === 'lens';
        const qty = Number(item.quantity ?? 1) || 1;
        const unit = item.unitPrice ?? item.unit_price;
        const line = item.totalPrice ?? item.total_price ?? (unit != null ? Number(unit) * qty : null);
        return `<div class="sold-row"><span class="kind">${escapeHtml(isLens ? L('lens') : L('frame'))}</span>`
          + `<span class="name">${escapeHtml(itemDisplayName(item))}</span>`
          + `<span class="price">${line != null ? escapeHtml(formatMoneyDzd(line)) : ''}</span></div>`;
      })
      .join('');
    const soldHtml = soldRows ? `<div class="sold">${soldRows}</div>` : '';

    const receiptQr = wantsQr
      ? `<img class="r-qr" src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=2&data=${encodeURIComponent(orderData.orderNumber || '')}" alt="QR ${orderNumber}" />`
      : '';

    // {{store}} is passed to i18n so the translated sentence carries the store
    // name, and the regex below still handles it for custom text.
    const termsText = localizedTemplateText(
      recTpl.collectionTermsText, DEFAULT_RECEIPT_TEMPLATE.collectionTermsText,
      'receiptFooter.collectionTerms', { store: b.storeName || '' },
    ).trim().replace(/\{\{\s*store\s*\}\}/gi, b.storeName || '');
    const termsHtml = (recTpl.showCollectionTerms !== false && termsText)
      ? `<div class="r-terms">${escapeHtml(termsText)}</div>`
      : '';

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${escapeHtml(pageTitle)} ${orderNumber}</title>
  <style>
    @page { size: A5 portrait; margin: 8mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      background: #f8fafc;
      color: #0f172a;
      font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
      font-size: ${bodyFontSize};
    }
    .paper {
      width: 148mm;
      min-height: 210mm;
      margin: 12px auto;
      padding: 10mm 12mm;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.10);
      display: flex;
      flex-direction: column;
    }

    /* Header: logo | store block | salesperson */
    .r-head { display: grid; grid-template-columns: 1fr 1.8fr 1fr; align-items: start; gap: 8px; }
    .r-logo-img { max-height: 62px; max-width: 108px; object-fit: contain; }
    .r-store { text-align: center; }
    .r-store .name {
      font-size: ${recTpl.storeNameSize === 'medium' ? '1.05rem' : '1.3rem'};
      font-weight: 800;
      color: ${b.primaryColor};
      line-height: 1.15;
    }
    .r-store .meta { font-size: 0.78rem; color: #475569; margin-top: 3px; line-height: 1.45; }
    .r-sales { text-align: right; font-size: 0.78rem; color: #475569; line-height: 1.5; }
    .r-sales .who { font-weight: 700; color: #0f172a; }

    .rule { border: none; border-top: 2px solid #0f172a; margin: 4px 0; }
    .slogan { text-align: center; font-size: 0.92rem; font-style: italic; color: #475569; padding: 2px 0; }

    .client-name { text-align: center; margin: 5px 0 2px; }
    .client-name .lbl { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8; font-weight: 700; }
    .client-name .val { font-size: 1.25rem; font-weight: 800; line-height: 1.2; }
    .client-name .ord { font-size: 0.8rem; font-weight: 700; color: ${b.primaryColor}; letter-spacing: 0.03em; margin-top: 2px; }

    /* Body: portrait stacks the details above the money block. The detail
       blocks pair up two-per-row so the tallest config (frame + lens both on,
       large font) still fits on a single A5 sheet. */
    .r-body { margin-top: 6px; flex: 1; }
    .r-details { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 10mm; }
    .blk.wide { grid-column: 1 / -1; }
    .blk .lbl { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; font-weight: 800; display: block; margin-bottom: 3px; }
    .blk .val { font-size: 0.95rem; font-weight: 700; }
    .rx-line { font-size: 0.92rem; font-weight: 700; line-height: 1.5; }
    .rx-line em { color: #64748b; font-weight: 800; font-style: normal; margin-right: 4px; }
    .blk .sub { display: block; font-size: 0.78rem; font-weight: 600; color: #475569; margin-top: 1px; }

    /* Priced list of what was sold, above the payment block */
    .sold { margin-top: 10px; display: flex; flex-direction: column; gap: 4px; }
    .sold-row { display: grid; grid-template-columns: 22mm 1fr auto; align-items: baseline; gap: 8px; padding-bottom: 5px; border-bottom: 1px dashed #e2e8f0; }
    .sold-row .kind { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; font-weight: 800; }
    .sold-row .name { font-size: 0.95rem; font-weight: 700; }
    .sold-row .price { font-size: 0.95rem; font-weight: 800; font-variant-numeric: tabular-nums; white-space: nowrap; }

    .money { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; }
    .money-row { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; padding-bottom: 5px; border-bottom: 1px dashed #e2e8f0; }
    .money-row .k { font-size: 0.8rem; color: #475569; font-weight: 600; }
    .money-row .v { font-size: 0.95rem; font-weight: 800; font-variant-numeric: tabular-nums; }
    .money-row.total { border-bottom: 2px solid #0f172a; padding-bottom: 6px; }
    .money-row.total .k, .money-row.total .v { font-size: 1.02rem; }
    .money-row.rest { border-bottom: none; }
    .money-row.rest .k, .money-row.rest .v { color: #dc2626; font-size: 1.05rem; font-weight: 800; }

    /* QR + stamp */
    .r-bottom { display: flex; align-items: flex-end; gap: 14px; margin-top: 6px; }
    .r-qr { width: 54px; height: 54px; }
    .stamp-box {
      width: 124px; height: 54px;
      border: 1.5px dashed #94a3b8; border-radius: 8px;
      display: flex; align-items: flex-start; padding: 5px 8px;
      color: #94a3b8; font-size: 0.66rem; font-weight: 800;
      text-transform: uppercase; letter-spacing: 0.05em;
    }

    .r-foot { margin-top: 6px; border-top: 1px solid #e2e8f0; padding-top: 6px; text-align: center; }
    .r-foot .thanks { font-size: 0.95rem; font-weight: 800; color: ${b.primaryColor}; }
    .r-terms, .r-policy { font-size: ${footerFontSize}; color: #64748b; line-height: 1.5; margin-top: 4px; max-width: 118mm; margin-left: auto; margin-right: auto; }
    @media print {
      body { background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .paper { width: auto; min-height: 0; margin: 0; border: none; box-shadow: none; border-radius: 0; padding: 0; }
    }
  </style>
</head>
<body>
  <div class="paper">
    <div class="r-head">
      <div>${receiptLogo}</div>
      <div class="r-store">
        <div class="name">${escapeHtml(b.storeName)}</div>
        ${storeMeta   ? `<div class="meta">${escapeHtml(storeMeta)}</div>` : ''}
        ${storeAddress ? `<div class="meta">${escapeHtml(storeAddress)}</div>` : ''}
      </div>
      <div class="r-sales">
        ${recTpl.showSalesPerson !== false && salesperson ? `<div class="who">${escapeHtml(salesperson)}</div>` : ''}
        ${recTpl.showDate !== false ? `<div>${escapeHtml(saleDay)}</div><div>${escapeHtml(saleHour)}</div>` : ''}
      </div>
    </div>

    <hr class="rule"/>
    ${(recTpl.showTagline !== false && b.tagline) ? `<div class="slogan">${escapeHtml(b.tagline)}</div>` : ''}
    <hr class="rule"/>

    <div class="client-name">
      ${recTpl.showCustomer !== false ? `<div class="lbl">${escapeHtml(L('customer'))}</div><div class="val">${customerLine}</div>` : ''}
      ${recTpl.showOrderNumber !== false ? `<div class="ord">${ordDisplay}</div>` : ''}
    </div>

    <div class="r-body">
      <div class="r-details">
        ${recTpl.showTimeToDeliver !== false ? `<div class="blk"><span class="lbl">${escapeHtml(tr('passport.expectedDelivery'))}</span>${deliveryHtml}</div>` : ''}
        ${hasCorrection ? `<div class="blk wide"><span class="lbl">${escapeHtml(L('correction'))}</span>
          ${rxLines}
        </div>` : ''}
      </div>

      ${soldHtml}

      <div class="money">
        ${recTpl.showPayment !== false ? moneyRow(L('payment'), paymentMethodLabel(pay.method || 'cash')) : ''}
        ${recTpl.showSubtotal && orderData.subtotal != null ? moneyRow(L('subtotal'), formatMoneyDzd(orderData.subtotal)) : ''}
        ${recTpl.showDiscount && orderData.discount ? moneyRow(L('discount'), formatMoneyDzd(orderData.discount)) : ''}
        ${recTpl.showAmountPaid !== false ? moneyRow(L('amountPaid'), formatMoneyDzd(deposit)) : ''}
        ${recTpl.showTotal !== false ? moneyRow(L('total'), formatMoneyDzd(orderData.total), 'total') : ''}
        ${recTpl.showRemainingBalance !== false ? moneyRow(L('remaining'), formatMoneyDzd(remaining), 'rest') : ''}
      </div>
    </div>

    <div class="r-bottom">
      ${receiptQr}
      ${recTpl.showStampBox !== false ? `<div class="stamp-box">${escapeHtml(L('stamp'))}</div>` : ''}
    </div>

    <div class="r-foot">
      <div class="thanks">${footerCopy}</div>
      ${termsHtml}
      ${returnPolicyHtml}
    </div>
  </div>
</body>
</html>`;
  }

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8"/>
  <title>${escapeHtml(pageTitle)} ${orderNumber}</title>
  <style>
    @page { size: A5 portrait; margin: 8mm; }
    * { box-sizing: border-box; }
    body {
      font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
      width: 148mm;
      min-height: 210mm;
      margin: 12px auto;
      padding: 8mm;
      color: #111;
      font-size: ${bodyFontSize};
      background: #fff;
    }

    /* ── Brand header ── */
    .brand-header {
      display: flex;
      align-items: center;
      gap: 14px;
      background: ${b.primaryColor};
      color: white;
      padding: 14px 18px;
      border-radius: 10px 10px 0 0;
      margin-bottom: 0;
      justify-content: ${headerAlign};
      text-align: ${headerTextAlign};
    }
    .store-logo {
      height: 44px;
      width: auto;
      max-width: 100px;
      object-fit: contain;
      border-radius: 6px;
      background: rgba(255,255,255,0.15);
      padding: 4px;
    }
    .brand-name  {
      font-size: ${(!isInvoice && recTpl.storeNameSize === 'medium') ? '1rem' : '1.15rem'};
      font-weight: 700;
      line-height: 1.2;
      margin: 0;
    }
    .brand-tag   { font-size: 0.78rem; opacity: 0.85; margin: 0; }
    .brand-meta  { font-size: 0.72rem; opacity: 0.85; margin: 2px 0 0; }

    /* ── Rest ── */
    .receipt-body { border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 10px 10px; padding: 18px 18px 12px; }
    h1 { font-size: 1.2rem; margin: 0 0 4px; color: ${b.primaryColor}; }
    .sub { color: #555; font-size: 0.9rem; margin-bottom: 20px; }
    .top { display: grid; grid-template-columns: 1fr auto; gap: 18px; align-items: start; }
    .badge { display:inline-block; padding:4px 8px; border-radius:999px; background:${badgeBg}; color:${badgeColor}; font-size:.75rem; font-weight:700; margin-bottom:8px; text-transform:uppercase; letter-spacing:.04em; }
    .info-grid { display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 18px; margin: 16px 0; }
    .info { border-bottom: 1px solid #eee; padding-bottom: 6px; }
    .label { display:block; color:#64748b; font-size:0.75rem; text-transform:uppercase; letter-spacing:.03em; }
    .value { display:block; font-weight:650; margin-top:2px; }
    table { width: 100%; border-collapse: collapse; margin: 16px 0; }
    th, td { border-bottom: 1px solid #ddd; padding: 8px 6px; text-align: left; font-size: 0.9rem; }
    th { background: #f6f6f6; }
    .totals { margin-top: 12px; font-size: 0.95rem; }
    .totals div { display: flex; justify-content: space-between; padding: 4px 0; }
    .strong { font-weight: 700; color: ${b.primaryColor}; }
    .accent  { color: ${b.accentColor}; }
    .qr-box { text-align:center; border:1px solid #ddd; border-radius:10px; padding:10px; min-width:132px; }
    .qr-img { display:block; width:150px; height:150px; }
    .qr-caption { font-size:.75rem; margin-top:8px; font-weight:700; word-break:break-all; }
    footer { margin-top: 20px; font-size: ${footerFontSize}; color: #888; border-top: 1px solid #f1f5f9; padding-top: 10px; }
    @media print {
      body {
        width: auto;
        min-height: calc(210mm - 16mm);
        margin: 0;
        padding: 0;
      }
    }
  </style>
</head>
<body>
  ${(logoHtml || b.storeName) ? `
  <div class="brand-header">
    ${logoHtml}
    <div>
      <p class="brand-name">${escapeHtml(b.storeName)}</p>
      ${(isInvoice ? invTpl.showTagline : recTpl.showTagline !== false) && b.tagline ? `<p class="brand-tag">${escapeHtml(b.tagline)}</p>` : ''}
      ${(isInvoice ? invTpl.showStorePhone : recTpl.showPhone) && b.phone ? `<p class="brand-meta">${escapeHtml(b.phone)}</p>` : ''}
      ${(isInvoice ? invTpl.showStoreAddress : recTpl.showAddress) && b.city  ? `<p class="brand-meta">${escapeHtml(b.city)}</p>`  : ''}
    </div>
  </div>
  ` : ''}

  <div class="receipt-body">
    <div class="top">
      <div>
        <div class="badge">${escapeHtml(badgeLabel)}</div>
        <h1>${escapeHtml(b.storeName)} — ${escapeHtml(heading)}</h1>
        <div class="sub">${dateStr}</div>
        <div class="info-grid">
          ${infoTiles}
        </div>
      </div>
      ${qr}
    </div>

    ${showItemsTable ? `
      <h2 style="font-size:1rem;margin-top:22px;color:${b.primaryColor}">${escapeHtml(L('itemsHeading'))}</h2>
      <table>
        <thead>${tableHead}</thead>
        <tbody>${rowsTpl || '<tr><td colspan="6">No line items</td></tr>'}${rxRow}</tbody>
      </table>
    ` : ''}

    ${totalsHtml}

    ${
      ((isInvoice && invTpl.showMeasurements) || (!isInvoice && recTpl.showMeasurements)) && measParts.length
        ? `<h2 style="font-size:1rem;margin-top:18px;color:${b.primaryColor}">${escapeHtml(L('measurementsHeading'))}</h2><p style="font-size:0.9rem;">${escapeHtml(measParts.join(' · '))}</p>`
        : ''
    }

    <footer>
      ${footerCopy}
      ${returnPolicyHtml}
      ${contactLine ? `<br/><span style="color:#aaa">${contactLine}</span>` : ''}
      ${websiteHtml}
    </footer>
  </div>
</body>
</html>`;
}

export async function downloadReceiptHtml({ orderData, paymentData, visitorData, copyType = 'customer', branding }) {
  if (!orderData) return;
  if (copyType === 'invoice') {
    const refs = await askReimbursementRefs(orderData);
    if (!refs) return;
    orderData = await ensureInvoiceNumber(orderData, refs);
  }
  const effectiveBranding = await resolvePrintBranding(orderData, branding);
  const html = buildReceiptHtml({ orderData, paymentData, visitorData, copyType, branding: effectiveBranding });
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  const safeNum  = String(orderData.orderNumber || 'order').replace(/[^\w.-]+/g, '_');
  const fileStem = copyType === 'logistics' ? 'Logistics-Receipt' : copyType === 'invoice' ? 'Invoice' : 'Customer-Receipt';
  a.href     = url;
  a.download = `${fileStem}-${safeNum}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function printReceiptHtml({ orderData, paymentData, visitorData, copyType = 'customer', branding }) {
  if (!orderData) return;
  // Invoice: ask for the reimbursement references first (the click on
  // "Print invoice" is what lets the print window open).
  let refs = null;
  if (copyType === 'invoice') {
    refs = await askReimbursementRefs(orderData);
    if (!refs) return;
  }
  const w = window.open('', '_blank', 'width=820,height=900');
  if (!w) {
    // Pop-up blocked: save the file instead (refs already typed, don't ask twice).
    const saved = copyType === 'invoice' ? await ensureInvoiceNumber(orderData, refs) : orderData;
    const html = buildReceiptHtml({ orderData: saved, paymentData, visitorData, copyType, branding: await resolvePrintBranding(saved, branding) });
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${copyType === 'invoice' ? 'Invoice' : 'Receipt'}-${String(orderData.orderNumber || 'order').replace(/[^\w.-]+/g, '_')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return;
  }
  w.document.open();
  w.document.write('<!DOCTYPE html><html><body style="font-family:system-ui;padding:24px">Preparing document...</body></html>');
  w.document.close();

  if (copyType === 'invoice') orderData = await ensureInvoiceNumber(orderData, refs);
  const effectiveBranding = await resolvePrintBranding(orderData, branding);
  const html = buildReceiptHtml({ orderData, paymentData, visitorData, copyType, branding: effectiveBranding });
  w.document.open();
  w.document.write(html);
  w.document.close();
  whenImagesLoaded(w).then(() => {
    try { w.focus(); w.print(); } catch (_) {}
  });
}

/**
 * Equipment Passport — an A5 landscape sheet that physically travels with the
 * frame through the workflow. A common header (store logo, client, order QR,
 * and the sold correction / lens / frame) sits on top, followed by three
 * stamp columns: MG Central, Lab, and Store. Each station fills its fields and
 * applies its stamp before handing the equipment on.
 */
export function buildEquipmentPassportHtml({ orderData, paymentData = {}, visitorData = {}, branding = {} }) {
  const b = { ...RECEIPT_DEFAULTS, ...branding };
  const orderNumber = escapeHtml(orderData.orderNumber || '—');

  const visitor = orderData.visitor;
  let clientName = fullName(visitor) || fullName(visitorData) || visitorData.name || '—';
  clientName = escapeHtml(clientName);

  // A two-pair sale ("Both": far + close up) prints one passport for both.
  const pairs = pairsOf(orderData);
  const frameDisplayText = pairs
    ? collectItems(orderData).filter((item) => (item.type || item.item_type) === 'frame').map(itemDisplayName).join(' + ')
    : frameSummaryDisplayName(orderData);
  const lensItem = findItem(orderData, 'lens');
  const { od, os } = getCorrectionLines(orderData);
  // Shared top-row content: correction lines + the sold lens name.
  const correctionLensHtml = pairs
    ? pairs.map((pair) => {
      const lines = getPairCorrectionLines(orderData, pair.visionPair);
      const pairLens = collectItems(orderData).find((item) => (item.type || item.item_type) === 'lens' && item.specifications?.visionPair === pair.visionPair);
      return [
        lines.od ? `OD: ${escapeHtml(lines.od)}` : '',
        lines.os ? `OS: ${escapeHtml(lines.os)}` : '',
        pairLens ? `<span style="color:${b.primaryColor};font-weight:700">${escapeHtml(itemDisplayName(pairLens))}</span>` : '',
      ].filter(Boolean).join('<br>');
    }).join('<br><br>')
    : [
      od ? `OD: ${escapeHtml(od)}` : '',
      os ? `OS: ${escapeHtml(os)}` : '',
      lensItem ? `<span style="color:${b.primaryColor};font-weight:700">${escapeHtml(itemDisplayName(lensItem))}</span>` : '',
    ].filter(Boolean).join('<br>');

  const salesperson = escapeHtml(fullName(orderData.salesPerson) || orderData.salesPerson?.employeeId || '');

  // Client phone + what is still owed, under the frame in the Frame sold cell,
  // so the store can call the client and collect the balance at pickup.
  // Same sources and arithmetic as the receipt (buildReceiptHtml): the
  // deposit lives in payment.amount — the paid_amount column is not kept up.
  const clientPhone = visitor?.phone || visitor?.phoneNumber || visitorData.phone || visitorData.phoneNumber
    || orderData.prescription?.phoneNumber || '';
  const orderTotal = Number(orderData.total || 0);
  const deposit = paymentData.deposit ?? orderData.payment?.amount ?? 0;
  const remaining = paymentData.remaining != null
    ? Number(paymentData.remaining)
    : Math.max(0, orderTotal - Number(deposit || 0));
  // Without a total there is nothing to subtract from: print no line rather
  // than a false "Paid in full".
  const hasBalanceInfo = paymentData.remaining != null || orderTotal > 0;
  const frameExtraLines = [
    clientPhone
      ? `<div class="fx-line"><span class="fx-lbl">${escapeHtml(tr('passport.clientPhone'))}</span><span class="fx-val">${escapeHtml(clientPhone)}</span></div>`
      : '',
    hasBalanceInfo
      ? `<div class="fx-line"><span class="fx-lbl">${escapeHtml(tr('passport.paid'))}</span><span class="fx-val">${escapeHtml(formatMoneyDzd(Number(deposit || 0)))}</span></div>`
      : '',
    hasBalanceInfo
      ? (remaining > 0
        ? `<div class="fx-line fx-due"><span class="fx-lbl">${escapeHtml(tr('passport.restToPay'))}</span><span class="fx-val">${escapeHtml(formatMoneyDzd(remaining))}</span></div>`
        : `<div class="fx-line fx-paid"><span class="fx-lbl">${escapeHtml(tr('passport.restToPay'))}</span><span class="fx-val">${escapeHtml(tr('passport.paidInFull'))}</span></div>`)
      : '',
  ].filter(Boolean).join('');
  const frameExtraHtml = frameExtraLines ? `<div class="frame-extra">${frameExtraLines}</div>` : '';

  // Two pairs: each measure prints as "far / close up".
  const measureOf = (key) => {
    const list = pairs ? pairs.map((pair) => pair.measurements || {}) : [orderData.measurements || {}];
    const values = list.map((ms) => (ms[key] != null && ms[key] !== '' ? String(ms[key]) : '—'));
    return values.every((v) => v === '—') ? '' : `${values.join(' / ')} mm`;
  };
  // Mounting height per eye ("OD 18 · OG 19"); older orders kept one value.
  const mountingOf = (ms) => {
    const has = (v) => v != null && v !== '';
    if (has(ms.mountingHeightOD) || has(ms.mountingHeightOG)) {
      return `OD ${has(ms.mountingHeightOD) ? ms.mountingHeightOD : '—'} · OG ${has(ms.mountingHeightOG) ? ms.mountingHeightOG : '—'}`;
    }
    return has(ms.mountingHeight) ? String(ms.mountingHeight) : '—';
  };
  const mountingValues = (pairs ? pairs.map((pair) => pair.measurements || {}) : [orderData.measurements || {}]).map(mountingOf);
  const mounting = mountingValues.every((v) => v === '—') ? '' : `${mountingValues.join(' / ')} mm`;
  const pdOD = measureOf('pdOD');
  const pdOG = measureOf('pdOG');

  // Frame adjustments the salesperson verified at frame selection. The flag
  // can live on the order column, inside raw_data, or (right after a sale) in
  // the sales session — pick whichever source actually has checks recorded.
  let sessionFrameChecks = {};
  try {
    const fsd = JSON.parse(sessionStorage.getItem('frameSelectionData') || '{}');
    // Numeric values (vertex/pantoscopic/wrap) live in adjustmentValues; toggles in adjustmentChecks
    sessionFrameChecks = { ...(fsd.adjustmentValues || {}), ...(fsd.adjustmentChecks || {}) };
  } catch (_) {}
  const adjSources = [
    orderData.frameAdjustmentsVerified,
    orderData.frame_adjustments_verified,
    orderData.raw_data?.frameAdjustmentsVerified,
    sessionFrameChecks,
  ];
  const primaryAdj = adjSources.find((o) => o && typeof o === 'object' && Object.values(o).some(Boolean)) || {};
  // Backfill numeric adjustment values from the session if the chosen source lacks them
  const adj = { ...primaryAdj };
  ['vertexDistance', 'pantoscopicAngle', 'wrapAngleGalbe'].forEach((k) => {
    const cur = adj[k];
    const hasVal = (typeof cur === 'string' && cur.trim() !== '') || typeof cur === 'number';
    const sessVal = sessionFrameChecks[k];
    const hasSess = (typeof sessVal === 'string' && sessVal.trim() !== '') || typeof sessVal === 'number';
    if (!hasVal && hasSess) adj[k] = sessVal;
  });
  const checkItem = (label, on) =>
    `<div class="chk-item"><span class="chk ${on ? 'on' : ''}"></span>${escapeHtml(label)}</div>`;
  // Like checkItem but adds a blank underline after the label for staff to
  // write on by hand (e.g. a date/note next to a contact-attempt checkbox).
  // `hint`: a faded reminder after the label (when to make that contact).
  const checkFillItem = (label, hint = '') =>
    `<div class="chk-item chk-fill-item"><span class="chk"></span>${escapeHtml(label)}${hint ? `<span class="chk-hint">${escapeHtml(hint)}</span>` : ''}<span class="chk-fill"></span></div>`;
  // Like checkItem but prints the measured value (e.g. "13 mm"). Falls back to a
  // plain check for legacy orders that stored a boolean instead of a number.
  const valueItem = (label, value, unit = '') => {
    const hasNumber = (typeof value === 'string' && value.trim() !== '') || typeof value === 'number';
    const on = hasNumber || value === true;
    const display = hasNumber ? `${escapeHtml(String(value).trim())}${escapeHtml(unit)}` : '';
    return `<div class="chk-item"><span class="chk ${on ? 'on' : ''}"></span>${escapeHtml(label)}${display ? `<span class="chk-val">${display}</span>` : ''}</div>`;
  };

  const logoHtml = b.logo
    ? `<img class="logo-img" src="${escapeHtml(b.logo.startsWith('http') ? b.logo : '/' + b.logo)}" alt="${escapeHtml(b.storeName)} logo" />`
    : `<div class="logo-name">${escapeHtml(b.storeName)}</div>`;

  const qrHtml = `<img class="qr" src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&margin=2&data=${encodeURIComponent(orderData.orderNumber || '')}" alt="QR ${orderNumber}" />`;

  const commonCell = (label, value, extra = '') =>
    `<div class="common-cell"><span class="lbl">${escapeHtml(label)}</span><span class="val">${value || '—'}</span>${extra}</div>`;

  const stationLine = (label, value) =>
    `<div class="st-line"><span class="st-lbl">${escapeHtml(label)}</span><span class="st-val">${value || ''}</span></div>`;

  // Time the sale was made (shown in the header under the salesperson).
  const saleTime = new Date(orderData.orderDate || orderData.createdAt || Date.now())
    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // Lead time of the sold lens, shown two ways: as a duration for Central (who
  // needs to know the window) and as a concrete date for the store (who tells
  // the client when to come back). Both blank out when the lens declares no
  // lead time, rather than printing a made-up promise.
  const deliveryDays = totalDeliveryDays(orderData);
  const trialLens = hasTrialLens(orderData);
  const deliverWithinText = deliveryDays
    ? tr('passport.daysCount', { count: deliveryDays })
    : '';
  const dueDate = expectedDeliveryDate(orderData);
  const expectedDeliveryText = dueDate ? formatDate(dueDate) : '';

  // Bottom of each column: a write-in Date/Time area next to the stamp box,
  // which carries the station's name faintly so each stamp lands in its box.
  // Each station writes when the equipment came IN and went OUT (date + hour),
  // then stamps.
  const ioLine = (tag) => `<div class="io-line">
      <span class="io-tag">${escapeHtml(tag)}</span>
      <span class="dt-mini"><span class="dt-mini-lbl">${escapeHtml(tr('passport.date'))}</span><span class="dt-mini-fill"></span></span>
      <span class="dt-mini"><span class="dt-mini-lbl">${escapeHtml(tr('passport.hour'))}</span><span class="dt-mini-fill"></span></span>
    </div>`;
  // The store is the last station — the equipment only comes IN there.
  const stampFooter = (station, { withOut = true } = {}) => `<div class="stamp-row">
    <div class="io">
      ${ioLine(tr('passport.in'))}
      ${withOut ? ioLine(tr('passport.out')) : ''}
    </div>
    <div class="stamp"><span>${escapeHtml(tr('passport.stamp'))}</span><span class="stamp-name">${escapeHtml(station)}</span></div>
  </div>`;

  const rtl = (i18n.language || 'en').split('-')[0] === 'ar';

  return `<!DOCTYPE html>
<html lang="${i18n.language || 'en'}" dir="${rtl ? 'rtl' : 'ltr'}">
<head>
  <meta charset="utf-8"/>
  <title>${escapeHtml(tr('passport.title'))} ${orderNumber}</title>
  <style>
    @page { size: A5 landscape; margin: 6mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
      color: #0f172a;
      background: #fff;
      font-size: 11px;
    }
    /* A5 landscape minus the 6mm @page margins leaves 136mm of printable
       height. Filling it exactly makes sub-pixel rounding spill onto a second
       sheet, so keep a few mm of slack and never break the sheet across pages. */
    .sheet { display: flex; flex-direction: column; width: 198mm; margin: 0 auto; min-height: 128mm; break-inside: avoid; page-break-inside: avoid; }

    /* Header */
    .hdr {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      gap: 10px;
      border: 2px solid ${b.primaryColor};
      border-radius: 8px;
      padding: 4px 12px;
    }
    .logo { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; }
    .logo-img { max-height: 46px; max-width: 130px; object-fit: contain; }
    .logo-name { font-size: 1.2rem; font-weight: 800; color: ${b.primaryColor}; }
    .store-sub { font-size: 0.78rem; font-weight: 700; color: #0f172a; }
    .sp { font-size: 0.72rem; font-weight: 600; color: #64748b; }
    /* Client, order number and store sit right next to the QR code. */
    .id-block { display: flex; align-items: center; gap: 12px; }
    .client { text-align: right; }
    .client .title { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; }
    .client .cn { font-size: 1.05rem; font-weight: 800; line-height: 1.15; }
    .client .ord { font-size: 0.85rem; font-weight: 700; color: ${b.primaryColor}; letter-spacing: 0.04em; }
    .qr-wrap { text-align: right; }
    .qr { width: 58px; height: 58px; }

    .passport-title {
      text-align: center;
      font-size: 0.74rem;
      font-weight: 800;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: ${b.primaryColor};
      margin: 2px 0 3px;
    }

    /* Common sold info */
    .common {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 8px;
      margin-bottom: 8px;
    }
    .common-cell {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 5px 9px;
      background: #f8fafc;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .common-cell .lbl { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; font-weight: 700; }
    .common-cell .val { font-weight: 700; font-size: 0.82rem; line-height: 1.25; }

    /* Workflow columns */
    .cols { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; flex: 1; }
    .col {
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 8px 9px;
      display: flex;
      flex-direction: column;
    }
    .col-title {
      text-align: center;
      font-size: 0.92rem;
      font-weight: 800;
      letter-spacing: 0.04em;
      color: #fff;
      background: ${b.primaryColor};
      border-radius: 6px;
      padding: 3px 0;
      margin-bottom: 8px;
    }
    .checks { margin-top: 4px; padding-top: 4px; border-top: 1px dashed #e2e8f0; display: flex; flex-direction: column; gap: 3px; }
    .chk-item { display: flex; align-items: center; font-size: 0.74rem; font-weight: 600; color: #475569; }
    .chk-fill-item { padding: 5px 0; border-bottom: 1px dashed #e2e8f0; }
    .chk-fill-item:last-of-type { border-bottom: none; }
    .chk-fill { flex: 1; margin-left: 8px; border-bottom: 1px solid #94a3b8; height: 12px; }
    .chk-hint { margin-left: 6px; color: #64748b; font-weight: 700; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .chk { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #94a3b8; border-radius: 3px; margin-right: 7px; position: relative; flex-shrink: 0; }
    .chk.on { background: ${b.primaryColor}; border-color: ${b.primaryColor}; }
    .chk.on::after { content: '✓'; color: #fff; font-size: 9px; line-height: 1; position: absolute; top: 0.5px; left: 1.5px; }
    .chk-val { margin-left: auto; padding-left: 8px; font-weight: 700; color: #1e293b; }
    .st-line { display: flex; justify-content: space-between; gap: 6px; padding: 3px 0; border-bottom: 1px dashed #e2e8f0; }
    .st-line:last-of-type { border-bottom: none; }
    .st-lbl { color: #64748b; font-weight: 600; }
    .st-val { font-weight: 700; text-align: right; word-break: break-word; }
    .dt-station { display: flex; align-items: center; gap: 12px; padding: 5px 0 8px; border-bottom: 1px dashed #e2e8f0; }
    .dt-station-lbl { color: #64748b; font-weight: 600; white-space: nowrap; font-size: 0.72rem; }
    .dt-station-fields { display: flex; gap: 14px; flex: 1; }
    .dt-mini { display: flex; align-items: baseline; gap: 5px; flex: 1; }
    .dt-mini-lbl { font-size: 0.6rem; text-transform: uppercase; color: #94a3b8; font-weight: 700; white-space: nowrap; }
    .dt-mini-fill { flex: 1; border-bottom: 1px solid #94a3b8; height: 16px; }
    .trial-box { display: flex; align-items: center; margin-top: 8px; padding: 6px 8px; border: 1.5px solid ${b.primaryColor}; border-radius: 8px; font-size: 0.82rem; font-weight: 800; }
    .stamp-row { margin-top: auto; display: flex; flex-direction: column; gap: 6px; }
    .io { display: flex; flex-direction: column; gap: 6px; }
    .io-line { display: flex; align-items: baseline; gap: 8px; }
    .io-tag { flex: 0 0 auto; min-width: 52px; white-space: nowrap; font-size: 0.66rem; font-weight: 800; letter-spacing: 0.05em; color: #475569; }
    .dt { flex: 1; display: flex; flex-direction: column; justify-content: flex-end; gap: 12px; padding-bottom: 4px; }
    .dt-line { display: flex; flex-direction: column; gap: 2px; }
    .dt-lbl { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.05em; color: #94a3b8; font-weight: 700; }
    .dt-fill { border-bottom: 1px solid #94a3b8; height: 14px; }
    .stamp {
      border: 1.5px dashed #94a3b8;
      border-radius: 8px;
      height: 40px;
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      padding: 4px 8px;
      color: #94a3b8;
      font-size: 0.66rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      gap: 8px;
    }
    .stamp-name { flex: 1; min-width: 0; align-self: center; text-align: center; font-size: 0.95rem; font-weight: 600; color: #cbd5e1; text-transform: none; letter-spacing: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .frame-extra { margin-top: 3px; padding-top: 3px; border-top: 1px dashed #e2e8f0; display: flex; flex-direction: column; gap: 1px; }
    .fx-line { display: flex; justify-content: space-between; align-items: baseline; gap: 6px; font-size: 0.76rem; }
    .fx-lbl { color: #64748b; font-weight: 600; }
    .fx-val { font-weight: 800; color: #0f172a; white-space: nowrap; }
    .fx-due .fx-val { color: ${b.primaryColor}; }
    .fx-paid .fx-val { color: #15803d; }
    @media print {
      html, body { margin: 0; padding: 0; height: auto; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* A trailing empty block is enough to emit a blank second sheet. */
      body > *:last-child { margin-bottom: 0; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="hdr">
      <div class="logo">
        ${logoHtml}
        ${b.logo ? `<div class="store-sub">${escapeHtml(b.storeName)}</div>` : ''}
        ${salesperson ? `<div class="sp">${escapeHtml(tr('passport.sales'))}: ${salesperson}</div>` : ''}
        <div class="sp">${escapeHtml(tr('passport.time'))}: ${escapeHtml(saleTime)}</div>
      </div>
      <div class="id-block">
        <div class="client">
          <div class="title">${escapeHtml(tr('passport.client'))}</div>
          <div class="cn">${clientName}</div>
          <div class="ord">${orderNumber}</div>
        </div>
        <div class="qr-wrap">${qrHtml}</div>
      </div>
    </div>

    <div class="passport-title">${escapeHtml(tr('passport.title'))}</div>

    <div class="common">
      ${commonCell(`${tr('passport.correction')} + ${tr('passport.lensSold')}`, correctionLensHtml)}
      ${commonCell(`${tr('passport.correction')} + ${tr('passport.lensSold')}`, correctionLensHtml)}
      ${commonCell(tr('passport.frameSold'), escapeHtml(frameDisplayText), frameExtraHtml)}
    </div>

    <div class="cols">
      <div class="col">
        <div class="col-title">${escapeHtml(tr('passport.mgCentral'))}</div>
        ${stationLine(tr('passport.frame'), escapeHtml(frameDisplayText))}
        ${stationLine(tr('passport.date'), escapeHtml(formatDate(orderData.orderDate || orderData.createdAt || Date.now())))}
        ${stationLine(tr('passport.deliverWithin'), escapeHtml(deliverWithinText))}
        ${trialLens ? `<div class="trial-box"><span class="chk on"></span>${escapeHtml(tr('passport.trialLens'))}</div>` : ''}
        ${stampFooter(tr('passport.mgCentral'))}
      </div>
      <div class="col">
        <div class="col-title">${escapeHtml(tr('passport.lab'))}</div>
        ${stationLine(tr('passport.odPd'), escapeHtml(pdOD))}
        ${stationLine(tr('passport.osPd'), escapeHtml(pdOG))}
        ${stationLine(tr('passport.hauteur'), escapeHtml(mounting))}
        <div class="checks">
          ${valueItem(tr('frameSelection.adj.vertexDistance'), adj.vertexDistance, ' mm')}
          ${valueItem(tr('frameSelection.adj.pantoscopicAngle'), adj.pantoscopicAngle, '°')}
          ${valueItem(tr('frameSelection.adj.wrapAngle'), adj.wrapAngleGalbe, '°')}
          ${checkItem(tr('frameSelection.adj.temple'), adj.branch)}
          ${checkItem(tr('frameSelection.adj.noseBridge'), adj.nez)}
        </div>
        ${stampFooter(tr('passport.lab'))}
      </div>
      <div class="col">
        <div class="col-title">${escapeHtml(tr('passport.store'))}</div>
        ${stationLine(tr('passport.expectedDelivery'), escapeHtml(expectedDeliveryText))}
        ${checkFillItem(tr('passport.contact1'))}
        ${checkFillItem(tr('passport.contact2'), tr('passport.afterDays', { count: 10 }))}
        ${checkFillItem(tr('passport.contact3'), tr('passport.afterDays', { count: 85 }))}
        ${checkFillItem(tr('passport.delivered'))}
        ${stampFooter(tr('passport.store'), { withOut: false })}
      </div>
    </div>
  </div>
  <script>
    // One A5 landscape page, always: 198 x 136 mm printable (6 mm margins).
    // When the content (measurements, contacts...) is taller than that,
    // shrink the whole sheet just enough to fit instead of spilling the
    // stamp row onto a second page.
    window.fitPassport = function () {
      var sheet = document.querySelector('.sheet');
      if (!sheet) return;
      sheet.style.zoom = '';
      var probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;visibility:hidden;height:100mm;width:1px';
      document.body.appendChild(probe);
      var pxPerMm = probe.getBoundingClientRect().height / 100;
      document.body.removeChild(probe);
      var maxH = 133 * pxPerMm;
      var h = sheet.getBoundingClientRect().height;
      if (h > maxH) sheet.style.zoom = String(Math.floor((maxH / h) * 1000) / 1000);
    };
    window.addEventListener('load', window.fitPassport);
    window.addEventListener('beforeprint', window.fitPassport);
  </script>
</body>
</html>`;
}

export async function printEquipmentPassport({ orderData, paymentData, visitorData, branding }) {
  if (!orderData) return;
  const w = window.open('', '_blank', 'width=900,height=720');
  const effectiveBranding = await resolvePrintBranding(orderData, branding);
  const html = buildEquipmentPassportHtml({ orderData, paymentData, visitorData, branding: effectiveBranding });
  if (!w) {
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeNum = String(orderData.orderNumber || 'order').replace(/[^\w.-]+/g, '_');
    a.href = url;
    a.download = `Equipment-Passport-${safeNum}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  // Print only once the QR code and logo have loaded (they come over the
  // network — a fixed delay sometimes printed without the QR), then fit
  // the sheet to one A5 page.
  const imagesReady = () => new Promise((resolve) => {
    const pending = Array.from(w.document.images || []).filter((img) => !img.complete);
    if (pending.length === 0) return resolve();
    let left = pending.length;
    const done = () => { left -= 1; if (left <= 0) resolve(); };
    pending.forEach((img) => { img.addEventListener('load', done); img.addEventListener('error', done); });
    setTimeout(resolve, 4000);
  });
  imagesReady().then(() => {
    try {
      if (typeof w.fitPassport === 'function') w.fitPassport();
      w.focus();
      w.print();
    } catch (_) {}
  });
}
